import React, { useMemo } from 'react';

export type LunchState = 'serving' | 'before' | 'after';
export interface LunchStatus { state: LunchState; minutesToOpen: number; }

const LUNCH_START_MIN = 630;  // 10:30 AM
const LUNCH_END_MIN = 1320;   // 10:00 PM

/** Status of Braum's lunch given the current minutes-since-midnight (Central time). */
export function lunchStatus(nowMinutes: number): LunchStatus {
  if (nowMinutes < LUNCH_START_MIN) return { state: 'before', minutesToOpen: LUNCH_START_MIN - nowMinutes };
  if (nowMinutes < LUNCH_END_MIN) return { state: 'serving', minutesToOpen: 0 };
  return { state: 'after', minutesToOpen: (1440 - nowMinutes) + LUNCH_START_MIN };
}

/** Current wall-clock minutes-since-midnight in America/Chicago (Braum's timezone),
 *  independent of the visitor's location. */
function chicagoMinutes(): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', hour12: false, hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date());
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return h * 60 + m;
}

const BraumsLunchStatus: React.FC = () => {
  const status = useMemo(() => lunchStatus(chicagoMinutes()), []);
  const h = Math.floor(status.minutesToOpen / 60);
  const m = status.minutesToOpen % 60;
  const countdown = h > 0 ? `${h}h ${m}m` : `${m}m`;

  const line =
    status.state === 'serving'
      ? '✅ Yes — Braum’s is serving lunch right now (until about 10:00 PM).'
      : status.state === 'before'
      ? `⏳ Not yet — Braum’s lunch starts at 10:30 AM (in ${countdown}).`
      : '🌙 Braum’s has stopped serving lunch for today. Lunch resumes tomorrow at 10:30 AM.';

  return (
    <div className="not-prose my-8 rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6">
      <h3 className="mt-0 mb-3 text-lg font-bold text-gray-900">Is Braum’s Serving Lunch Right Now?</h3>
      <p className="text-2xl font-bold text-gray-900 mb-2">{line}</p>
      <p className="mb-1 text-gray-700">Lunch is served <strong>10:30 AM – 10:00 PM</strong>, every day.</p>
      <p className="mb-0 text-sm text-gray-500">Hours vary by location; status is based on US Central Time.</p>
    </div>
  );
};

export default BraumsLunchStatus;
